import React, { useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Button, Chip, Stack } from '@mui/material';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import { MemberStatus, ClinicalFlag, FLAG_COLORS } from '../Data/enums';

const getClinicalFlags = (row) => {
  const flags = [];
  if (row.isAdmitted) flags.push(ClinicalFlag.ADMITTED);
  if (row.isDischarged) flags.push(ClinicalFlag.DISCHARGED);
  if (row.isEmergency) flags.push(ClinicalFlag.EMERGENCY);
  if (row.isHighCost) flags.push(ClinicalFlag.HIGH_COST);
  if (row.isHighRisk) flags.push(ClinicalFlag.HIGH_RISK);
  return flags;
};

export default function PatientDataGrid({
  rows = [],
  loading = false,
  rowCount = 0,
  paginationModel,
  onPaginationModelChange,
  onSelectPatient,
}) {
  const columns = useMemo(() => [
    {
      field: 'memberFullName',
      headerName: 'Patient Name',
      flex: 2,
      filterable: false,
    },
    {
      field: 'memberId',
      headerName: 'Member ID',
      flex: 1,
      filterable: false,
    },
    {
      field: 'dateOfBirth',
      headerName: 'DOB',
      flex: 1,
      filterable: false,
      valueFormatter: (params) => {
        if (!params) return '—';
        const d = new Date(params);
        if (isNaN(d.getTime())) return String(params);
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      },
    },
    {
      field: 'gender',
      headerName: 'Gender',
      flex: 0.6,
      filterable: false,
    },
    {
      field: 'pcpName',
      headerName: 'PCP',
      flex: 1.5,
      filterable: false,
    },
    {
      field: 'medicalCenter',
      headerName: 'Medical Center',
      flex: 1.5,
      filterable: false,
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.8,
      filterable: false,
      renderCell: (params) => {
        const isInactive = params.value === MemberStatus.INACTIVE;
        return (
          <Chip
            size="small"
            icon={isInactive ? <PersonOffOutlinedIcon /> : undefined}
            label={isInactive ? 'Inactive' : 'Active'}
            color={isInactive ? 'error' : 'success'}
            variant="outlined"
            sx={{ borderRadius: 2 }}
          />
        );
      },
    },
    {
      field: 'clinicalFlags',
      headerName: 'Clinical Flags',
      flex: 2,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const flags = getClinicalFlags(params.row);
        if (flags.length === 0) return '—';
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center" sx={{ py: 0.5 }}>
            {flags.map((flag) => (
              <Chip
                key={flag}
                size="small"
                label={flag}
                color={FLAG_COLORS[flag] || 'default'}
                sx={{ borderRadius: 2, fontSize: '0.7rem' }}
              />
            ))}
          </Stack>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <Button
          size="small"
          variant="contained"
          onClick={() => onSelectPatient?.(params.row)}
        >
          Select
        </Button>
      ),
    },
  ], [onSelectPatient]);

  return (
    <div style={{ width: '100%' }}>
      <DataGrid
        autoHeight
        rows={rows}
        columns={columns}
        rowCount={rowCount}
        loading={loading}
        paginationMode="server"
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[25, 50, 100]}
        density="compact"
        disableColumnFilter
        disableColumnSelector
        disableRowSelectionOnClick
        sx={{
          border: '1px solid #ccc',
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#607d8b',
            color: '#fff',
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-row': {
            backgroundColor: '#fff',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: '#f5f5f5',
          },
          '& .MuiDataGrid-row:hover': {
            backgroundColor: '#e3f2fd',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #eee',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          '& .MuiDataGrid-footerContainer': {
            justifyContent: 'flex-start',
          },
        }}
      />
    </div>
  );
}
